<script setup>
import {computed, onMounted, useTemplateRef} from 'vue';
import {
    ArrowPathRoundedSquareIcon,
    SparklesIcon as SparklesIconOut,
    TagIcon as TagIconOut,
} from '@heroicons/vue/24/outline';
import {SparklesIcon as SparklesIconSolid, TagIcon as TagIconSolid} from '@heroicons/vue/24/solid';
import {
    activeServices,
    DEFAULT_MAP_CENTER,
    formatDistance,
    geolocated,
    radii,
    radius,
    searchCoordinates,
    services,
} from '../../stores/storeMap';
import {getCurrentPosition} from '../../utils';

/**
 * @type {import('vue').TemplateRef<google.maps.places.PlaceAutocompleteElement>}
 */
const autocomplete = useTemplateRef('autocomplete');
const focusRing = computed(() => findAutocompleteChild(value => {
    return value instanceof HTMLDivElement && value.classList.contains('focus-ring');
}));
const clearButton = computed(() => findAutocompleteChild(value => {
    return value instanceof HTMLButtonElement && value.classList.contains('clear-button');
}));

/**
 * @template T
 * @param {function(T, string): boolean} predicate
 * @return {T|null}
 */
function findAutocompleteChild(predicate) {
    for (const [key, value] of Object.entries(autocomplete.value)) {
        if (predicate(value, key)) {
            return value;
        }
    }
    return null;
}

function reset() {
    searchCoordinates.value = null;
    geolocated.value = false;
    autocomplete.value.value = '';
}

async function locateUser() {
    try {
        const position = await getCurrentPosition();
        reset();
        searchCoordinates.value = position;
        geolocated.value = true;
    } catch {
    }
}

/**
 * @param {google.maps.places.PlaceAutocompletePlaceSelectEvent} placePredictionSelectEvent
 * @returns {Promise<void>}
 */
async function onAutocompleteSelect({placePrediction}) {
    const place = placePrediction.toPlace();
    await place.fetchFields({fields: ['location']});
    searchCoordinates.value = place.location.toJSON();
    geolocated.value = false;
}

onMounted(() => {
    autocomplete.value.locationBias = DEFAULT_MAP_CENTER;
    if (clearButton.value) { // Remove clear button
        clearButton.value.style.display = 'none';
    }
    if (focusRing.value) { // Remove focus ring
        focusRing.value.style.display = 'none';
    }
});
</script>

<template>
    <form class="store-list-search">
        <div class="row column-reverse">
            <div class="column small-4 store-list-search-location">
                <button type="button"
                        class="button"
                        :class="{'is-active': geolocated}"
                        @click="locateUser()">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" width="24" fill="currentColor">
                        <path
                            d="m8.407 0-.002 1.662.044.005a6.38 6.38 0 0 1 5.522 5.041l.052.29.007.051 1.97.002v1.356h-1.913v.033a6.376 6.376 0 0 1-5.345 5.853l-.337.043L8.407 16H7.05l-.002-1.664-.038-.003a6.375 6.375 0 0 1-5.594-5.449l-.034-.297-.014-.183L0 8.407V7.051l1.426-.002.008-.051a6.38 6.38 0 0 1 5.281-5.29l.334-.046L7.051 0zm-.678 2.847a5.153 5.153 0 1 0 0 10.306 5.153 5.153 0 0 0 0-10.306m0 2.17a2.983 2.983 0 1 1 0 5.966 2.983 2.983 0 0 1 0-5.966"/>
                    </svg>
                    <span class="sr-only">Me localiser</span>
                </button>
                <gmp-place-autocomplete ref="autocomplete"
                                        placeholder="Rechercher une adresse&hellip;"
                                        included-region-codes="fr"
                                        requested-language="fr-FR"
                                        requested-region="fr"
                                        @gmp-select="onAutocompleteSelect"/>
                <template v-if="searchCoordinates">
                    <div class="store-list-search-radius">
                        <label for="store-list-radius">Distance :</label>
                        <select id="store-list-radius" v-model="radius">
                            <option v-for="distance in radii" :value="distance">{{ formatDistance(distance) }}</option>
                        </select>
                    </div>
                    <button type="button"
                            class="button"
                            @click="reset()">
                        <svg width="21" height="20" fill="currentColor" viewBox="0 0 21 20">
                            <path fill-rule="evenodd"
                                  d="M10.8 0a10 10 0 100 20 10 10 0 100-20Zm2.59 6L10.8 8.59 8.21 6 6.8 7.41 9.39 10 6.8 12.59 8.21 14l2.59-2.59L13.39 14l1.41-1.41L12.21 10l2.59-2.59L13.39 6ZM2.8 10a8.01 8.01 0 0016 0 8.01 8.01 0 00-16 0Z"
                                  clip-rule="evenodd"></path>
                        </svg>
                        <span class="sr-only">Réinitialiser</span>
                    </button>
                </template>
            </div>
            <div class="column small-4 store-list-services">
                <label>Services</label>
                <ul class="store-list-services-list">
                    <li v-for="(label, service) in services" :key="service">
                        <label :for="`store-services-${service}`">
                            <input type="checkbox"
                                   name="store-services"
                                   :id="`store-services-${service}`"
                                   :value="service"
                                   v-model="activeServices">
                            <ArrowPathRoundedSquareIcon v-if="service === 'certified'"/>
                            <component :is="activeServices.includes(service) ? SparklesIconSolid : SparklesIconOut"
                                       v-else-if="service === 'manufactured'"/>
                            <component :is="activeServices.includes(service) ? TagIconSolid : TagIconOut"
                                       v-else-if="service === 'voucher'"/>
                            <span>{{ label }}</span>
                        </label>
                    </li>
                </ul>
            </div>
        </div>
    </form>
</template>
